import { useCallback, useMemo, useState } from "react";
import {
  BASE_URL,
  DEFAULT_END_DATE,
  DEFAULT_QUERY_OPTIONS,
  DEFAULT_START_DATE,
  DEFAULT_TIME_INTERVAL,
  ENDPOINT,
} from "./constants";
import axios from "axios";
import { useQuery } from "react-query";
import { lighten } from "@material-ui/core";

/* ---------- Generic Fetch Hook ---------- */
const useFetch = ({ endpoint, queryKey = null, params = "", options = {} }) => {
  const queryFn = async () => {
    const { data } = await axios.get(`${BASE_URL}/${endpoint}/${params}`);
    return data;
  };

  const queryArray = queryKey === null ? [endpoint] : [queryKey];
  return useQuery(queryArray, queryFn, {
    onError: (error) => console.error(error),
    ...DEFAULT_QUERY_OPTIONS,
    ...options,
  });
};

/* ---------- Round Helper ---------- */
const round = (v) => (v == null ? null : Math.round(v * 100) / 100);

/* ---------- Prepare Dynamic Stacked Graph Data ---------- */
const prepareGraphData = (rows = []) => {
  if (!rows.length) return { labels: [], datasets: [] };

  const bySeries = {};
  const labelsSet = new Set();
  const measuredMap = new Map();

  rows.forEach((r) => {
    const d = new Date(r.collect_date).toISOString().slice(0, 10);
    labelsSet.add(d);

    if (!measuredMap.has(d)) {
      measuredMap.set(d, round(r.measured_flow));
    }

    const key = r.cow_input_ndx;
    if (!bySeries[key]) {
      bySeries[key] = {
        label: r.cow_input_name,
        yAxisID: "yL",
        type: "line",
        backgroundColor: r.graph_color,
        borderColor: r.graph_color,
        fill: "origin",
        pointRadius: 0,
        pointHoverRadius: 0,
        spanGaps: false,
        stack: "cow",
        pointStyle: "rect",
        legendAndTooltipOrder: r.cow_slice_order ?? key,
        sliceOrder: r.cow_slice_order ?? key,
        data: [],
      };
    }

    bySeries[key].data.push(round(r.cow_slice_cfs));
  });

  const labels = Array.from(labelsSet)
    .sort()
    .map((d) => new Date(`${d}T00:00:00`));
  const measured = {
    label: "Measured Flow",
    type: "line",
    yAxisID: "yL",
    backgroundColor: lighten("#000000", 0.5),
    borderColor: "#000000",
    data: labels.map(
      (d) => measuredMap.get(d.toISOString().slice(0, 10)) ?? null
    ),
    pointBorderWidth: 2,
    pointBackgroundColor: "#0F9ED5",
    pointBorderColor: "#000000",
    pointRadius: 4,
    pointHoverRadius: 6,
    borderWidth: 5,
    spanGaps: false,
    stack: false,
    legendAndTooltipOrder: 999,
  };

  return {
    labels,
    datasets: [
      measured,
      ...Object.values(bySeries).sort((a, b) => a.sliceOrder - b.sliceOrder),
    ],
  };
};

/* ---------- Hook ---------- */
export const useColorsOfWaterElkhead = () => {
  const [timeInterval, setTimeInterval] = useState(DEFAULT_TIME_INTERVAL);
  const [dateRange, setDateRange] = useState({
    startDate: DEFAULT_START_DATE,
    endDate: DEFAULT_END_DATE,
  });

  const handleTimeInterval = useCallback(({ target: { value } }) => {
    setTimeInterval(value);
  }, []);
  const handleStartDate = useCallback((date) => {
    setDateRange((prev) => ({ ...prev, startDate: date }));
  }, []);
  const handleEndDate = useCallback((date) => {
    setDateRange((prev) => ({ ...prev, endDate: date }));
  }, []);

  const {
    data,
    error,
    isFetching: isLoading,
    refetch,
  } = useFetch({
    endpoint: `${ENDPOINT}/graph-${timeInterval}`,
    queryKey: [
      "line",
      dateRange.startDate.toISOString().slice(0, 10),
      dateRange.endDate.toISOString().slice(0, 10),
    ],
    params: `${dateRange.startDate
      .toISOString()
      .slice(0, 10)}/${dateRange.endDate.toISOString().slice(0, 10)}`,
  });

  const graphData = useMemo(() => prepareGraphData(data), [data]);

  return {
    timeInterval,
    setTimeInterval,
    handleTimeInterval,
    dateRange,
    setDateRange,
    handleStartDate,
    handleEndDate,
    rawData: data,
    data: graphData,
    error,
    isLoading,
    refetch,
  };
};
